import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Home, IdCard, Key, LogOut, Mail, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProfileBasedMenu, useUserProfile } from "@/components/ProfileBasedMenu";
import { ProfileSelector } from "@/components/ProfileSelector";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useEnvironment } from "@/hooks/useEnvironment";
import { getAppName } from "@/utils/appConfig";

export const API_CALLS_SECTION_ID = "historico-chamadas";

const MENU_ROUTES: Record<string, string> = {
  "cadastrar-cliente": "/clients",
  "cadastrar-workspace": "/workspaces",
  "gerenciar-workspace": "/workspaces",
  "gerenciar-perfis": "/profiles",
  "gerenciar-usuarios": "/users",
  "gerenciar-usuarios-workspace": "/users",
  "visualizar-usuarios": "/users",
  "provedores-ia": "/ai-providers",
  "validacao-acesso": "/access-validation",
  auditoria: "/audit",
  "modelos-ia": "/settings",
  agentes: "/agents",
  especialistas: "/products",
  planos: "/plans",
  sandbox: "/sandbox",
  relatorios: "/reports",
  "relatorios-workspace": "/reports",
  dashboard: "/",
  "dashboard-pessoal": "/",
  "api-cliente": "/client-api",
};

const API_CALLS_MENU_ITEMS = new Set([
  "visualizar-logs",
  "chamadas-api",
  "chamadas-api-seguranca",
]);

export const AppHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { currentProfile, changeProfile } = useUserProfile();
  const { showProfileSelector } = useEnvironment();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const scrollToApiCalls = () => {
    document
      .getElementById(API_CALLS_SECTION_ID)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleMenuItemClick = (itemId: string) => {
    if (API_CALLS_MENU_ITEMS.has(itemId)) {
      if (location.pathname === "/") {
        scrollToApiCalls();
      } else {
        navigate("/", { state: { scrollTo: API_CALLS_SECTION_ID } });
      }
      return;
    }

    const route = MENU_ROUTES[itemId];
    if (route) {
      navigate(route);
    }
  };

  const handleGoHome = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    navigate("/");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              {getAppName()}
            </h1>
            <p className="text-xs text-muted-foreground">
              Dashboard de Monitoramento
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {showProfileSelector && (
            <ProfileSelector
              currentProfile={currentProfile}
              onProfileChange={changeProfile}
            />
          )}

          <Button
            size="icon"
            onClick={handleGoHome}
            className="bg-gradient-primary text-white shadow-sm hover:opacity-90"
            title="Início"
            aria-label="Ir para o dashboard"
          >
            <Home className="h-4 w-4" />
          </Button>

          <ProfileBasedMenu
            profileId={currentProfile}
            profileName={user?.profile?.name || user?.profile_name}
            onMenuItemClick={handleMenuItemClick}
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="hover:bg-primary/10"
                title="Perfil de Usuário"
              >
                <IdCard className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-64 bg-popover border shadow-lg z-50"
            >
              <div className="px-4 py-3 border-b">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Perfil de Usuário</p>
                    <p className="text-xs text-muted-foreground flex items-center">
                      <Mail className="h-3 w-3 mr-1" />
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>
              <div className="py-2">
                <div className="px-4 py-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Informações de Acesso
                  </p>
                  <div className="mt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Perfil Atual:</span>
                      <Badge variant="secondary" className="text-xs">
                        {user?.profile?.name || user?.profile_name || currentProfile}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Status:</span>
                      <Badge
                        variant="secondary"
                        className="text-xs bg-green-100 text-green-800"
                      >
                        Ativo
                      </Badge>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Último Login:</span>
                      <span className="text-xs">
                        {new Date().toLocaleString("pt-BR")}
                      </span>
                    </div>
                  </div>
                </div>
                {user?.access_type === "password" && (
                  <div className="px-4 py-2 border-t">
                    <DropdownMenuItem
                      onClick={() => setChangePasswordOpen(true)}
                      className="cursor-pointer"
                    >
                      <Key className="h-4 w-4 mr-2" />
                      <span>Alterar Senha</span>
                    </DropdownMenuItem>
                  </div>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <ChangePasswordDialog
            open={changePasswordOpen}
            onOpenChange={setChangePasswordOpen}
          />

          <Button
            variant="outline"
            size="icon"
            onClick={handleLogout}
            className="hover:bg-destructive/10 hover:text-destructive"
            title="Sair"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};
